import { formatDistanceMeters, formatDurationSeconds, haversineKm, decodePolyline, type RouteSummary } from '@motogo/shared';
import { ChevronDown, ChevronRight, ChevronUp, Plus, Search, SlidersHorizontal, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type FC } from 'react';
import { useNavigate } from 'react-router-dom';

import PlaceSearch from './PlaceSearch';
import RideSparkline from './RideSparkline';

import { useDiscover } from '@/hooks/useDiscover';
import { useRoutePlan } from '@/hooks/useRoutePlan';
import { searchRoutes, type RouteSearchParams } from '@/lib/savedRoutes';

const RADII = [25, 50, 100, 250] as const;

const LENGTHS: { label: string; minKm?: number; maxKm?: number }[] = [
  { label: 'Any' },
  { label: '<100', maxKm: 100 },
  { label: '100–300', minKm: 100, maxKm: 300 },
  { label: '>300', minKm: 300 },
];

const TIMES: { label: string; minMinutes?: number; maxMinutes?: number }[] = [
  { label: 'Any' },
  { label: '<2 h', maxMinutes: 120 },
  { label: '2–5 h', minMinutes: 120, maxMinutes: 300 },
  { label: '>5 h', minMinutes: 300 },
];

const CHARACTERS = [
  { value: 'scenic', label: 'Scenic' },
  { value: 'curvy', label: 'Curvy' },
  { value: 'fast', label: 'Fast' },
  { value: 'gravel', label: 'Gravel' },
];

/**
 * Discover (M4): browse public routes anywhere — /discover, an overlay in the editor
 * pattern (the map behind stays live; panning + "Search this area" is the loop). Results
 * live in the DiscoverProvider so opening a route detail and coming back keeps the list;
 * MapView draws every hit's overview line and frames the focused one.
 */
const DiscoverPage: FC = () => {
  const navigate = useNavigate();
  const discover = useDiscover();
  const { positionRef } = useRoutePlan();

  const [radiusKm, setRadiusKm] = useState(50);
  const [lengthIdx, setLengthIdx] = useState(0);
  const [timeIdx, setTimeIdx] = useState(0);
  const [characters, setCharacters] = useState<string[]>([]);
  // Collapsed by default: browsing is the main loop, the knobs are the exception —
  // folded away, the sheet leads with the search bar and the results.
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Mark the discover surface active for MapView while mounted.
  const { setActive } = discover;
  useEffect(() => {
    setActive(true);
    return () => setActive(false);
  }, [setActive]);
  useEffect(() => () => abortRef.current?.abort(), []);

  const runSearch = async (center: { lat: number; lon: number } | null) => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setSearching(true);
    setError(null);
    try {
      const params: RouteSearchParams = {
        lat: center?.lat,
        lon: center?.lon,
        radiusKm,
        minKm: LENGTHS[lengthIdx].minKm,
        maxKm: LENGTHS[lengthIdx].maxKm,
        minMinutes: TIMES[timeIdx].minMinutes,
        maxMinutes: TIMES[timeIdx].maxMinutes,
        character: characters.length > 0 ? characters : undefined,
        take: 50,
      };
      const results = await searchRoutes(params, ctrl.signal);
      discover.setResults(results, center);
    } catch (e) {
      if (ctrl.signal.aborted) return;
      setError(e instanceof Error ? e.message : 'Search failed');
    } finally {
      if (!ctrl.signal.aborted) setSearching(false);
    }
  };

  const searchThisArea = () => {
    const center = discover.mapCenterRef.current ?? positionRef.current;
    void runSearch(center ? { lat: center.lat, lon: center.lon } : null);
  };

  // First open: search around wherever the map is, once, unprompted.
  const autoSearched = useRef(false);
  useEffect(() => {
    if (autoSearched.current || discover.results !== null) return;
    autoSearched.current = true;
    searchThisArea();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleCharacter = (value: string) =>
    setCharacters((current) =>
      current.includes(value) ? current.filter((c) => c !== value) : [...current, value],
    );

  // "x km away": nearest overview-polyline point to the search center, per result.
  const distancesKm = useMemo(() => {
    const center = discover.searchCenter;
    const map = new Map<string, number>();
    if (!center || !discover.results) return map;
    for (const r of discover.results) {
      let best = Infinity;
      for (const [lat, lon] of decodePolyline(r.overviewPolyline)) {
        const d = haversineKm(center.lat, center.lon, lat, lon);
        if (d < best) best = d;
      }
      if (Number.isFinite(best)) map.set(r.id, best);
    }
    return map;
  }, [discover.results, discover.searchCenter]);

  const cardMeta = (route: RouteSummary): string => {
    const parts: string[] = [];
    if (route.ownerName) parts.push(`by ${route.ownerName}`);
    parts.push(formatDistanceMeters(route.distanceMeters));
    if (route.durationSeconds !== null) parts.push(formatDurationSeconds(route.durationSeconds));
    const away = distancesKm.get(route.id);
    if (away !== undefined) parts.push(away < 1 ? 'right here' : `${Math.round(away)} km away`);
    return parts.join(' · ');
  };

  // What the collapsed filter row says about the active knobs, at a glance.
  const filterSummary = [
    `${radiusKm} km`,
    lengthIdx > 0 ? `${LENGTHS[lengthIdx].label} km` : null,
    timeIdx > 0 ? TIMES[timeIdx].label : null,
    ...characters.map((value) => CHARACTERS.find((c) => c.value === value)?.label ?? value),
  ]
    .filter((part): part is string => part !== null)
    .join(' · ');

  return (
    <div className="mg-editor">
      <header className="mg-editor-header">
        <button className="mg-icon-btn" onClick={() => navigate('/')} aria-label="Close discover" title="Close discover">
          <X size={26} />
        </button>
        <h1 className="mg-editor-title">Discover routes</h1>
        <button
          className="mg-icon-btn"
          onClick={() => navigate('/routes/new')}
          aria-label="Create a route"
          title="Create a route"
        >
          <Plus size={26} />
        </button>
      </header>

      <div className="mg-editor-panel mg-discover-panel">
        {/* Address bar + area-search on one row — the stacked full-width button made the
            panel needlessly tall on phones and comically so on desktops. */}
        <div className="mg-discover-search-row">
          <div style={{ flex: 1, minWidth: 0 }}>
            <PlaceSearch
              placeholder="Search a place — anywhere"
              selected={null}
              onSelect={(place) => {
                if (!place) return;
                discover.flyTo(place.lat, place.lon);
                void runSearch({ lat: place.lat, lon: place.lon });
              }}
              biasRef={positionRef}
            />
          </div>
          <button
            className="mg-btn mg-btn-primary mg-discover-search-btn"
            onClick={searchThisArea}
            disabled={searching}
            aria-label="Search this area"
            title="Search this area"
          >
            <Search size={20} />
          </button>
        </div>

        <button className="mg-filter-toggle" onClick={() => setFiltersOpen((o) => !o)} aria-expanded={filtersOpen}>
          <SlidersHorizontal size={18} />
          <span className="mg-filter-toggle-summary">{filterSummary}</span>
          {filtersOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>

        {filtersOpen && (
          <>
            <div className="mg-setting-row" style={{ minHeight: 0 }}>
              <span className="mg-setting-label">Within</span>
              <div className="mg-segment">
                {RADII.map((r) => (
                  <button
                    key={r}
                    className={radiusKm === r ? 'mg-segment-btn active' : 'mg-segment-btn'}
                    onClick={() => setRadiusKm(r)}
                  >
                    {r} km
                  </button>
                ))}
              </div>
            </div>

            <div className="mg-setting-row" style={{ minHeight: 0 }}>
              <span className="mg-setting-label">Length</span>
              <div className="mg-segment">
                {LENGTHS.map((l, i) => (
                  <button
                    key={l.label}
                    className={lengthIdx === i ? 'mg-segment-btn active' : 'mg-segment-btn'}
                    onClick={() => setLengthIdx(i)}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mg-setting-row" style={{ minHeight: 0 }}>
              <span className="mg-setting-label">Time</span>
              <div className="mg-segment">
                {TIMES.map((t, i) => (
                  <button
                    key={t.label}
                    className={timeIdx === i ? 'mg-segment-btn active' : 'mg-segment-btn'}
                    onClick={() => setTimeIdx(i)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mg-setting-row" style={{ minHeight: 0 }}>
              <span className="mg-setting-label">Character</span>
              <div className="mg-segment">
                {CHARACTERS.map((c) => (
                  <button
                    key={c.value}
                    className={characters.includes(c.value) ? 'mg-segment-btn active' : 'mg-segment-btn'}
                    onClick={() => toggleCharacter(c.value)}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {searching && <div className="mg-plan-hint">Searching…</div>}
        {error && <div className="mg-plan-error">{error}</div>}

        {discover.results && discover.results.length === 0 && !searching && (
          <div className="mg-plan-hint">No public routes here yet — widen the radius or search another area.</div>
        )}

        {discover.results && discover.results.length > 0 && (
          <ul className="mg-ride-list" style={{ padding: 0, overflow: 'visible', flex: 'none' }}>
            {discover.results.map((route) => (
              <li key={route.id}>
                <div
                  className={
                    route.id === discover.focusedId ? 'mg-ride-card mg-discover-card focused' : 'mg-ride-card mg-discover-card'
                  }
                  role="button"
                  tabIndex={0}
                  onClick={() =>
                    // First tap frames it on the map; a second tap opens it.
                    route.id === discover.focusedId
                      ? navigate(`/routes/${route.id}`)
                      : discover.setFocusedId(route.id)
                  }
                  onKeyDown={(e) => {
                    // Mirror the click ladder for keyboard: first activation frames, second
                    // opens. Space is a button-activation key too — prevent it scrolling.
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      if (route.id === discover.focusedId) navigate(`/routes/${route.id}`);
                      else discover.setFocusedId(route.id);
                    }
                  }}
                >
                  <RideSparkline polyline={route.overviewPolyline} width={72} height={44} />
                  <div className="mg-ride-meta">
                    <span className="mg-ride-name">{route.name}</span>
                    <span className="mg-ride-date">{cardMeta(route)}</span>
                  </div>
                  <button
                    className="mg-icon-btn"
                    style={{ marginLeft: 'auto' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/routes/${route.id}`);
                    }}
                    aria-label={`Open ${route.name}`}
                    title="Open route"
                  >
                    <ChevronRight size={22} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default DiscoverPage;
